import React, { useEffect, useMemo, useState } from 'react';
import { Table } from 'react-bootstrap';
import Select from 'react-select';

import { SortableTableHeader, useSortableTable } from '../../../../components/sortable-table-header';
import { TransportOrderStatusBadge } from '../../../../components/TransportOrderStatusBadge';
import { CurrencyRecord, SupportedCurrency } from '../../../../domain/currency';
import { buildOrderMonthYearRange, StatsFilters } from '../../../../domain/statsFilters';
import { TransportOrder } from '../../../../domain/transportOrder';
import { useTransportOrdersRepository } from '../../../../repository/transport-orders/useTransportOrdersRepository';
import { calculateProfitForCurrency } from '../../../../utils/calculateProfitForCurrency';
import { TransportOrderLinkWithPreview } from '../../../transport-order-details-popover/TransportOrderLinkWithPreview';

type SortMode = 'orderId' | 'loading' | 'unloading' | 'clientValue' | 'freightValue' | 'profit';

type TransportOrdersStatsForMonthProps = {
    transportOrders: Array<TransportOrder>;
    forCurrency: SupportedCurrency;
    currencyRecord: CurrencyRecord;
}

export const TransportOrdersStatsByMonth = ({ filters, currencyRecord }: {
    filters: StatsFilters;
    currencyRecord: CurrencyRecord;
}) => {
    const monthsList = useMemo(() => {
        const orderMonthYearRange = buildOrderMonthYearRange(filters);

        const monthsList: Array<{ month: number; year: number }> = [];
        if (orderMonthYearRange) {
            const { fromMonth, fromYear, toMonth, toYear } = orderMonthYearRange;
            let currentYear = fromYear;
            let currentMonth = fromMonth;

            while (currentYear < toYear || (currentYear === toYear && currentMonth <= toMonth)) {
                monthsList.push({ month: currentMonth, year: currentYear });
                currentMonth++;
                if (currentMonth > 12) {
                    currentMonth = 1;
                    currentYear++;
                }
            }
        }

        return monthsList;
    }, [filters]);

    const [selectedTransportOrdersMonth, setSelectedTransportOrdersMonth] = useState<string>(monthsList[0].month + '-' + monthsList[0].year);

    const options = monthsList.map(({ month, year }) => ({
        value: `${month}-${year}`,
        label: `${month}/${year}`,
        metadata: { month, year },
    }));

    useEffect(() => {
        setSelectedTransportOrdersMonth(options[0].value);
    }, [filters]);

    const selectedValue = options.find(({ value }) => value === selectedTransportOrdersMonth);

    const { data: transportOrders } = useTransportOrdersRepository({
        contractor: filters.contractor ? { id: filters.contractor.id } : undefined,
        creator: filters.creator ? { id: filters.creator.id } : undefined,
        orderMonthYearRange: selectedValue && {
            fromMonth: selectedValue.metadata.month,
            fromYear: selectedValue.metadata.year,
            toMonth: selectedValue.metadata.month,
            toYear: selectedValue.metadata.year,
        },
    });

    if (!transportOrders) {
        return null;
    }

    return <>
        <div className="d-flex justify-content-between align-items-center mb-3">
            <h4>Zlecenia {selectedValue?.label} ({transportOrders.length})</h4>
            {monthsList.length > 1 && <Select
                // className="d-inline-block"
                value={options.find(option => option.value === selectedTransportOrdersMonth)}
                options={options}
                onChange={(newValue) => setSelectedTransportOrdersMonth(newValue?.value as string)}
                placeholder="Miesiąc"
            />}
        </div>
        <TransportOrdersStatsForSingleMonth
            transportOrders={transportOrders}
            currencyRecord={currencyRecord}
            forCurrency={'EUR'}
        />
    </>;
};

const TransportOrdersStatsForSingleMonth = ({
    transportOrders,
    forCurrency,
    currencyRecord,
}: TransportOrdersStatsForMonthProps) => {
    const { sortBy, sortDirection, handleSort, sortItems } = useSortableTable<SortMode>({
        initialSortBy: 'orderId',
        initialDirection: 'asc',
        comparator: (a, b, sortKey, direction) => {
            const multiplier = direction === 'desc' ? 1 : -1;
            switch (sortKey) {
                case 'orderId':
                    return multiplier * a.id.localeCompare(b.id);
                case 'loading':
                    return multiplier * (a.loading[0]?.date.getTime() - b.loading[0]?.date.getTime());
                case 'unloading':
                    return multiplier * (a.unloading[0]?.date.getTime() - b.unloading[0]?.date.getTime());
                case 'clientValue':
                    return multiplier * (Number(a.freight.clientValue) - Number(b.freight.clientValue));
                case 'freightValue':
                    return multiplier * (Number(a.freight.value) - Number(b.freight.value));
                case 'profit':
                    const profitA = calculateProfitForCurrency(a, forCurrency, currencyRecord);
                    const profitB = calculateProfitForCurrency(b, forCurrency, currencyRecord);
                    return multiplier * (profitA - profitB);
                default:
                    return 0;
            }
        },
    });

    const activeOrders = sortItems(transportOrders.filter(({ status }) => status !== 'DRAFT'));

    if (activeOrders.length === 0) {
        return <p className="d-flex align-self-center">Brak zleceń.</p>;
    }

    return (
        <>
            <Table striped bordered hover responsive>
                <thead>
                <tr>
                    <SortableTableHeader
                        label="#"
                        sortKey="orderId"
                        currentSortBy={sortBy}
                        sortDirection={sortDirection}
                        onSort={handleSort}
                    />
                    <th className="text-center">Status</th>
                    <SortableTableHeader
                        label="Załadunek"
                        sortKey="loading"
                        currentSortBy={sortBy}
                        sortDirection={sortDirection}
                        onSort={handleSort}
                    />
                    <SortableTableHeader
                        label="Rozładunek"
                        sortKey="unloading"
                        currentSortBy={sortBy}
                        sortDirection={sortDirection}
                        onSort={handleSort}
                    />
                    <SortableTableHeader
                        label="Oferta klienta"
                        sortKey="clientValue"
                        currentSortBy={sortBy}
                        sortDirection={sortDirection}
                        onSort={handleSort}
                    />
                    <SortableTableHeader
                        label="Fracht"
                        sortKey="freightValue"
                        currentSortBy={sortBy}
                        sortDirection={sortDirection}
                        onSort={handleSort}
                    />
                    <SortableTableHeader
                        label="Marża"
                        sortKey="profit"
                        currentSortBy={sortBy}
                        sortDirection={sortDirection}
                        onSort={handleSort}
                    />
                </tr>
                </thead>
                <tbody>
                {activeOrders.map((transportOrder) => {
                    const profit = calculateProfitForCurrency(transportOrder, forCurrency, currencyRecord);

                    return (
                        <tr key={transportOrder.id}>
                            <td className="width-0">
                                <div className="d-flex align-items-center justify-content-center">
                                    <TransportOrderLinkWithPreview transportOrder={transportOrder} />
                                </div>
                            </td>
                            <td className="text-center width-0">
                                <TransportOrderStatusBadge status={transportOrder.status} />
                            </td>
                            <td className="text-center">
                                {buildDate(transportOrder.loading)}
                            </td>
                            <td className="text-center">
                                {buildDate(transportOrder.unloading)}
                            </td>
                            <td className="text-center width-0">
                                {isNaN(Number(transportOrder.freight.clientValue)) ? <span className="fst-italic">Brak</span> :
                                    Number(transportOrder.freight.clientValue).toLocaleString('pl-PL', {
                                        style: 'currency',
                                        currency: transportOrder.freight.clientCurrency,
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                    })}
                            </td>
                            <td className="text-center width-0">
                                {Number(transportOrder.freight.value).toLocaleString('pl-PL', {
                                    style: 'currency',
                                    currency: transportOrder.freight.currency,
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                })}
                            </td>
                            <td className={`text-center width-0 ${transportOrder.status === 'STORNO' ? 'text-decoration-line-through text-secondary' : profit >= 0 ? 'text-success' : 'text-danger'}`}>
                                {profit.toLocaleString('pl-PL', {
                                    style: 'currency',
                                    currency: forCurrency,
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                })}
                            </td>
                        </tr>
                    );
                })}
                </tbody>
            </Table>
        </>
    );
};


const buildText = (from: Date, to: Date) => {
    const fromText = dateToString(from);
    const toText = dateToString(to);

    return `${fromText}${fromText === toText ? '' : `-${toText}`}`;
};

const dateToString = (date: Date) =>
    date.toLocaleDateString('pl-PL', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    });

const buildDate = (items: TransportOrder['loading'] | TransportOrder['unloading']) => {
    if (items.length === 1) {
        return buildText(items[0].date, items[0].endDate);
    }

    const firstDateFrom = [...items].sort((a, b) => a.date.getTime() - b.date.getTime())[0];
    const lastDateTo = [...items].sort((a, b) => b.endDate.getTime() - a.endDate.getTime())[0];

    return buildText(firstDateFrom.date, lastDateTo.endDate);
};
